import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DashboardLayout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTrial } from '../contexts/TrialContext';
import api from '../lib/api';
import { goPro } from '../lib/checkout';
import { ArrowRight, Headphones, BookOpen, PenTool, Mic, FileText, Lock, Unlock } from 'lucide-react';

const skillMeta = {
  listening: { icon: <Headphones size={24} />, color: 'bg-blue-500', name: 'Listening', desc: 'Practice all 6 listening parts of the CELPIP test. Improve your comprehension skills.' },
  reading: { icon: <BookOpen size={24} />, color: 'bg-amber-500', name: 'Reading', desc: 'Practice all 4 reading parts. Master correspondence, diagrams, and viewpoints.' },
  writing: { icon: <PenTool size={24} />, color: 'bg-rose-500', name: 'Writing', desc: 'Practice email writing and survey responses with AI scoring.' },
  speaking: { icon: <Mic size={24} />, color: 'bg-emerald-500', name: 'Speaking', desc: 'Practice all 8 speaking tasks with voice recording and AI feedback.' },
};

// Parts that have free content
const FREE_PARTS = ['L1', 'L2', 'R1', 'R2', 'W1', 'S1', 'S2'];

export default function SkillPractice() {
  const { skill } = useParams();
  const { user } = useAuth();
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = skillMeta[skill] || { icon: <FileText size={24} />, color: 'bg-neutral-500', name: skill, desc: '' };
  const isPro = user?.subscription_status && user.subscription_status !== 'free';
  const { entryLabel } = useTrial();

  useEffect(() => {
    api.get(`/api/parts?skill_id=${skill}`).then(r => setParts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [skill]);

  return (
    <DashboardLayout>
      <div data-testid="skill-practice-page">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className={`w-12 h-12 rounded-xl ${meta.color} text-white flex items-center justify-center`}>{meta.icon}</div>
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">{meta.name} Practice</h1>
              <p className="text-neutral-500 text-sm">{meta.desc}</p>
            </div>
          </div>
        </div>

        {!isPro && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between" data-testid="pro-upgrade-banner">
            <div>
              <p className="text-sm font-medium text-amber-800">{entryLabel} Plan — Limited Practice Questions</p>
              <p className="text-xs text-amber-600 mt-0.5">Upgrade to Pro for all 3,000+ questions and AI scoring</p>
            </div>
            <button onClick={() => goPro()} className="px-4 py-2 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 transition" data-testid="upgrade-btn">
              Upgrade to Pro
            </button>
          </div>
        )}

        {loading ? (
          <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-neutral-100 rounded-xl animate-pulse" />)}</div>
        ) : (
          <div className="space-y-3">
            {parts.map((part, idx) => {
              const isFree = FREE_PARTS.includes(part.id);
              const isLocked = !isPro && !isFree;
              const Wrapper = isLocked ? 'div' : Link;
              const wrapperProps = isLocked ? {} : { to: `/practice/${skill}/${part.id}` };

              return (
                <Wrapper key={part.id} {...wrapperProps}
                  className={`flex items-center gap-4 p-5 bg-white rounded-xl border transition group ${
                    isLocked
                      ? 'border-neutral-200 opacity-60 cursor-not-allowed'
                      : 'border-neutral-200 hover:border-primary/30 hover:shadow-sm cursor-pointer'
                  }`}
                  data-testid={`part-card-${part.id}`}>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
                    isLocked ? 'bg-neutral-100 text-neutral-400' : 'bg-primary/10 text-primary'
                  }`}>
                    {part.part_number || idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-medium ${isLocked ? 'text-neutral-500' : 'text-neutral-900'}`}>{part.name}</h3>
                      {isFree && <span className="px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 rounded">{entryLabel}</span>}
                      {isLocked && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-neutral-100 text-neutral-500 rounded flex items-center gap-0.5">
                          <Lock size={8} />Pro
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-500">{part.description}</p>
                  </div>
                  <div className="text-sm text-neutral-400 flex items-center gap-1">
                    {Math.floor((part.time_limit_seconds || 0) / 60)} min
                    {isLocked ? (
                      <Lock size={16} className="ml-2 text-neutral-300" />
                    ) : (
                      <ArrowRight size={16} className="ml-2 text-neutral-300 group-hover:text-primary transition" />
                    )}
                  </div>
                </Wrapper>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
